import { db } from '@/services/storage/db';
import { compressImage } from '@/utils/image-utils';
import { queuePhotoDelete } from './photo-sync-service';
import { recordPendingDelete } from './sync-service';
import { extractExifFromFile } from './exif-service';
import type { PhotoRecord } from '@/types/photo';

export const MAX_PHOTOS_PER_ENTRY = 10;

/** Maximum number of photos that can be attached to a hunt trip. */
export const MAX_HUNT_PHOTOS = 25;

/**
 * Compress and store photos for a log entry. Updates LogEntry.photos[] with the new IDs.
 * Throws if adding files would exceed MAX_PHOTOS_PER_ENTRY.
 */
export async function addPhotosToEntry(
  logEntryId: string,
  userId: string,
  files: File[],
): Promise<PhotoRecord[]> {
  if (files.length === 0) return [];

  const existingCount = await db.photos.where('logEntryId').equals(logEntryId).count();
  if (existingCount + files.length > MAX_PHOTOS_PER_ENTRY) {
    throw new Error(
      `Cannot add ${files.length} photo(s): would exceed the maximum of ${MAX_PHOTOS_PER_ENTRY} per entry (${existingCount} already attached).`,
    );
  }

  const records: PhotoRecord[] = [];
  for (const file of files) {
    const compressed = await compressImage(file);
    const now = new Date().toISOString();
    const compressedBytes = await compressed.blob.arrayBuffer();
    const record: PhotoRecord = {
      id: crypto.randomUUID(),
      logEntryId,
      userId,
      blobBytes: compressedBytes,
      blobType: compressed.blob.type || 'image/jpeg',
      blobSize: compressed.blob.size,
      mimeType: 'image/jpeg',
      originalFilename: file.name,
      width: compressed.width,
      height: compressed.height,
      sizeBytes: compressed.sizeBytes,
      createdAt: now,
      updatedAt: now,
    };
    await db.photos.put(record);
    records.push(record);
  }

  // Update the LogEntry.photos array with new IDs
  const entry = await db.logEntries.get(logEntryId);
  if (!entry) {
    throw new Error(`addPhotosToEntry: log entry record ${logEntryId} not found`);
  }
  const updatedPhotoIds = [...(entry.photos ?? []), ...records.map((r) => r.id)];
  await db.logEntries.put({ ...entry, photos: updatedPhotoIds, updatedAt: new Date().toISOString() });

  return records;
}

/** Return all photos for a log entry, ordered by createdAt ascending. */
export async function getPhotosForEntry(logEntryId: string): Promise<PhotoRecord[]> {
  const results = await db.photos.where('logEntryId').equals(logEntryId).toArray();
  return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Return a single photo by ID, or null if not found. */
export async function getPhoto(photoId: string): Promise<PhotoRecord | null> {
  return (await db.photos.get(photoId)) ?? null;
}

/**
 * Delete a photo record and remove its ID from the parent LogEntry.photos array.
 */
export async function deletePhoto(photoId: string, logEntryId: string): Promise<void> {
  await db.photos.delete(photoId);
  await queuePhotoDelete(photoId);
  // Queue Postgres metadata soft-delete ('photos' plural = Postgres row; 'photo' singular = Storage bucket)
  await recordPendingDelete(photoId, 'photos');
  const entry = await db.logEntries.get(logEntryId);
  if (entry) {
    const updatedPhotoIds = (entry.photos ?? []).filter((id) => id !== photoId);
    await db.logEntries.put({ ...entry, photos: updatedPhotoIds, updatedAt: new Date().toISOString() });
  }
}

/**
 * Delete all photos for a log entry. Called when the entry itself is deleted
 * to prevent orphaned blobs accumulating in IndexedDB.
 */
export async function deletePhotosForEntry(logEntryId: string): Promise<void> {
  const photos = await db.photos.where('logEntryId').equals(logEntryId).toArray();
  await db.photos.where('logEntryId').equals(logEntryId).delete();
  for (const photo of photos) {
    await queuePhotoDelete(photo.id);
    await recordPendingDelete(photo.id, 'photos');
  }
}

/** Return total compressed size in bytes for all photos belonging to a user. */
export async function getPhotoStorageUsage(userId: string): Promise<number> {
  const photos = await db.photos.where('userId').equals(userId).toArray();
  return photos
    .filter((p) => p.id && typeof p.id === 'string')
    .reduce((sum, p) => sum + p.sizeBytes, 0);
}

/** Return total photo count for a user. */
export async function getPhotoCount(userId: string): Promise<number> {
  const photos = await db.photos.where('userId').equals(userId).toArray();
  return photos.filter((p) => p.id && typeof p.id === 'string').length;
}

// ---------------------------------------------------------------------------
// Camera location photos (M42)
// ---------------------------------------------------------------------------

/** Return all photos for a camera waypoint, ordered by createdAt ascending. */
export async function getPhotosByWaypoint(waypointId: string): Promise<PhotoRecord[]> {
  const results = await db.photos.where('waypointId').equals(waypointId).toArray();
  return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Compress a file, extract EXIF, and store the photo record attached to a waypoint.
 * Extracts EXIF before compression so the original file data is intact.
 */
export async function addPhotoToWaypoint(
  waypointId: string,
  userId: string,
  file: File,
): Promise<PhotoRecord> {
  const [exifData, compressed] = await Promise.all([
    extractExifFromFile(file),
    compressImage(file),
  ]);

  const now = new Date().toISOString();
  const compressedBytes = await compressed.blob.arrayBuffer();
  const record: PhotoRecord = {
    id: crypto.randomUUID(),
    waypointId,
    userId,
    blobBytes: compressedBytes,
    blobType: compressed.blob.type || 'image/jpeg',
    blobSize: compressed.blob.size,
    mimeType: 'image/jpeg',
    originalFilename: file.name,
    width: compressed.width,
    height: compressed.height,
    sizeBytes: compressed.sizeBytes,
    createdAt: now,
    updatedAt: now,
    exifData,
  };
  await db.photos.put(record);
  return record;
}

/**
 * Delete a camera waypoint photo record.
 * Does NOT need to update a parent entity's photos[] array (unlike log entry photos).
 */
export async function deleteWaypointPhoto(photoId: string): Promise<void> {
  await db.photos.delete(photoId);
  await queuePhotoDelete(photoId);
  await recordPendingDelete(photoId, 'photos');
}

/** Delete all photos for a camera waypoint. Called when the waypoint is deleted. */
export async function deletePhotosForWaypoint(waypointId: string): Promise<void> {
  const photos = await db.photos.where('waypointId').equals(waypointId).toArray();
  await db.photos.where('waypointId').equals(waypointId).delete();
  for (const photo of photos) {
    await queuePhotoDelete(photo.id);
    await recordPendingDelete(photo.id, 'photos');
  }
}

// ---------------------------------------------------------------------------
// Hunt trip photos (M49)
// ---------------------------------------------------------------------------

/** Return all photos for a hunt trip, ordered by createdAt ascending. */
export async function getPhotosForHunt(huntId: string): Promise<PhotoRecord[]> {
  const results = await db.photos.where('huntId').equals(huntId).toArray();
  return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Compress and store photos attached to a hunt trip.
 * Updates Hunt.photos[] with the new IDs.
 */
export async function addPhotosToHunt(
  huntId: string,
  userId: string,
  files: File[],
): Promise<PhotoRecord[]> {
  if (files.length === 0) return [];

  const existingCount = await db.photos.where('huntId').equals(huntId).count();
  if (existingCount + files.length > MAX_HUNT_PHOTOS) {
    throw new Error(
      `Cannot add ${files.length} photo(s): would exceed the maximum of ${MAX_HUNT_PHOTOS} per hunt (${existingCount} already attached).`,
    );
  }

  const records: PhotoRecord[] = [];
  for (const file of files) {
    const compressed = await compressImage(file);
    const now = new Date().toISOString();
    const compressedBytes = await compressed.blob.arrayBuffer();
    const record: PhotoRecord = {
      id: crypto.randomUUID(),
      huntId,
      userId,
      blobBytes: compressedBytes,
      blobType: compressed.blob.type || 'image/jpeg',
      blobSize: compressed.blob.size,
      mimeType: 'image/jpeg',
      originalFilename: file.name,
      width: compressed.width,
      height: compressed.height,
      sizeBytes: compressed.sizeBytes,
      createdAt: now,
      updatedAt: now,
    };
    await db.photos.put(record);
    records.push(record);
  }

  // Update the Hunt.photos array with new IDs
  const hunt = await db.hunts.get(huntId);
  if (!hunt) {
    throw new Error(`addPhotosToHunt: hunt record ${huntId} not found`);
  }
  const updatedPhotoIds = [...(hunt.photos ?? []), ...records.map((r) => r.id)];
  await db.hunts.put({ ...hunt, photos: updatedPhotoIds, updatedAt: new Date().toISOString() });

  return records;
}

/**
 * Delete a hunt photo and remove its ID from the parent Hunt.photos array.
 */
export async function deleteHuntPhoto(photoId: string, huntId: string): Promise<void> {
  await db.photos.delete(photoId);
  await queuePhotoDelete(photoId);
  await recordPendingDelete(photoId, 'photos');
  const hunt = await db.hunts.get(huntId);
  if (hunt) {
    const updatedPhotoIds = (hunt.photos ?? []).filter((id) => id !== photoId);
    await db.hunts.put({ ...hunt, photos: updatedPhotoIds, updatedAt: new Date().toISOString() });
  }
}

/** Delete all photos for a hunt. Called when the hunt is deleted. */
export async function deletePhotosForHunt(huntId: string): Promise<void> {
  const photos = await db.photos.where('huntId').equals(huntId).toArray();
  await db.photos.where('huntId').equals(huntId).delete();
  for (const photo of photos) {
    await queuePhotoDelete(photo.id);
    await recordPendingDelete(photo.id, 'photos');
  }
}

// ---------------------------------------------------------------------------
// Harvest record photos (M58)
// ---------------------------------------------------------------------------

/** Return all photos for a harvest record, ordered by createdAt ascending. */
export async function getPhotosForHarvest(harvestId: string): Promise<PhotoRecord[]> {
  const results = await db.photos.where('harvestId').equals(harvestId).toArray();
  return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Compress and store photos attached to a harvest record.
 * Updates HarvestRecord.photos[] with the new IDs.
 * Auto-sets HarvestRecord.thumbnailPhotoId to the first photo's ID when currently unset.
 * Throws if adding files would exceed MAX_PHOTOS_PER_ENTRY.
 */
export async function addPhotosToHarvest(
  harvestId: string,
  userId: string,
  files: File[],
): Promise<PhotoRecord[]> {
  if (files.length === 0) return [];

  const existingCount = await db.photos.where('harvestId').equals(harvestId).count();
  if (existingCount + files.length > MAX_PHOTOS_PER_ENTRY) {
    throw new Error(
      `Cannot add ${files.length} photo(s): would exceed the maximum of ${MAX_PHOTOS_PER_ENTRY} per harvest (${existingCount} already attached).`,
    );
  }

  const records: PhotoRecord[] = [];
  for (const file of files) {
    const compressed = await compressImage(file);
    const now = new Date().toISOString();
    const compressedBytes = await compressed.blob.arrayBuffer();
    const record: PhotoRecord = {
      id: crypto.randomUUID(),
      harvestId,
      userId,
      blobBytes: compressedBytes,
      blobType: compressed.blob.type || 'image/jpeg',
      blobSize: compressed.blob.size,
      mimeType: 'image/jpeg',
      originalFilename: file.name,
      width: compressed.width,
      height: compressed.height,
      sizeBytes: compressed.sizeBytes,
      createdAt: now,
      updatedAt: now,
    };
    await db.photos.put(record);
    records.push(record);
  }

  // Update the HarvestRecord.photos array and auto-set thumbnailPhotoId if unset
  const harvest = await db.harvestRecords.get(harvestId);
  if (!harvest) {
    throw new Error(`addPhotosToHarvest: harvest record ${harvestId} not found`);
  }
  const updatedPhotoIds = [...(harvest.photos ?? []), ...records.map((r) => r.id)];
  const thumbnailPhotoId = harvest.thumbnailPhotoId ?? records[0]?.id;
  await db.harvestRecords.put({
    ...harvest,
    photos: updatedPhotoIds,
    thumbnailPhotoId,
    updatedAt: new Date().toISOString(),
  });

  return records;
}

/**
 * Delete a harvest photo and remove its ID from the parent HarvestRecord.photos array.
 * Clears HarvestRecord.thumbnailPhotoId if the deleted photo was the thumbnail.
 */
export async function deleteHarvestPhoto(photoId: string, harvestId: string): Promise<void> {
  await db.photos.delete(photoId);
  await queuePhotoDelete(photoId);
  await recordPendingDelete(photoId, 'photos');
  const harvest = await db.harvestRecords.get(harvestId);
  if (harvest) {
    const updatedPhotoIds = (harvest.photos ?? []).filter((id) => id !== photoId);
    const thumbnailPhotoId =
      harvest.thumbnailPhotoId === photoId ? undefined : harvest.thumbnailPhotoId;
    await db.harvestRecords.put({
      ...harvest,
      photos: updatedPhotoIds,
      thumbnailPhotoId,
      updatedAt: new Date().toISOString(),
    });
  }
}

/**
 * Delete all photos for a harvest record.
 * Called when the harvest record itself is deleted to prevent orphaned blobs.
 */
export async function deletePhotosForHarvest(harvestId: string): Promise<void> {
  const photos = await db.photos.where('harvestId').equals(harvestId).toArray();
  await db.photos.where('harvestId').equals(harvestId).delete();
  for (const photo of photos) {
    await queuePhotoDelete(photo.id);
    await recordPendingDelete(photo.id, 'photos');
  }
}

// ---------------------------------------------------------------------------
// Hunt sit photos (M69)
// ---------------------------------------------------------------------------

/** Return all photos for a specific hunt sit, ordered by createdAt ascending. */
export async function getPhotosBySitId(sitId: string): Promise<PhotoRecord[]> {
  const results = await db.photos.where('sitId').equals(sitId).toArray();
  return results.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Compress and store photos attached to a hunt sit.
 * Tags photos with both `sitId` and `huntId` for aggregated gallery queries.
 * Throws if adding files would exceed MAX_PHOTOS_PER_ENTRY.
 */
export async function addPhotosToSit(
  sitId: string,
  huntId: string,
  userId: string,
  files: File[],
): Promise<PhotoRecord[]> {
  if (files.length === 0) return [];

  const existingCount = await db.photos.where('sitId').equals(sitId).count();
  if (existingCount + files.length > MAX_PHOTOS_PER_ENTRY) {
    throw new Error(
      `Cannot add ${files.length} photo(s): would exceed the maximum of ${MAX_PHOTOS_PER_ENTRY} per sit (${existingCount} already attached).`,
    );
  }

  const records: PhotoRecord[] = [];
  for (const file of files) {
    const compressed = await compressImage(file);
    const now = new Date().toISOString();
    const compressedBytes = await compressed.blob.arrayBuffer();
    const record: PhotoRecord = {
      id: crypto.randomUUID(),
      sitId,
      huntId,
      userId,
      blobBytes: compressedBytes,
      blobType: compressed.blob.type || 'image/jpeg',
      blobSize: compressed.blob.size,
      mimeType: 'image/jpeg',
      originalFilename: file.name,
      width: compressed.width,
      height: compressed.height,
      sizeBytes: compressed.sizeBytes,
      createdAt: now,
      updatedAt: now,
    };
    await db.photos.put(record);
    records.push(record);
  }

  // Bump sit's updatedAt so SitCard's useSitPhotos hook re-runs via the sit prop dependency.
  const sit = await db.huntSits.get(sitId);
  if (sit) {
    await db.huntSits.put({ ...sit, updatedAt: new Date().toISOString() });
  }

  return records;
}

/**
 * Delete a single sit photo by ID.
 * Removes the record from IndexedDB and queues for remote deletion on next sync.
 */
export async function deleteSitPhoto(photoId: string): Promise<void> {
  const photo = await db.photos.get(photoId);
  await db.photos.delete(photoId);
  await queuePhotoDelete(photoId);
  await recordPendingDelete(photoId, 'photos');
  // Bump sit's updatedAt so SitCard's useSitPhotos hook re-runs via the sit prop dependency.
  if (photo?.sitId) {
    const sit = await db.huntSits.get(photo.sitId);
    if (sit) {
      await db.huntSits.put({ ...sit, updatedAt: new Date().toISOString() });
    }
  }
}

/**
 * Delete all photos for a hunt sit.
 * Called when the sit is deleted to prevent orphaned blobs.
 */
export async function deletePhotosForSit(sitId: string): Promise<void> {
  const photos = await db.photos.where('sitId').equals(sitId).toArray();
  await db.photos.where('sitId').equals(sitId).delete();
  for (const photo of photos) {
    await queuePhotoDelete(photo.id);
    await recordPendingDelete(photo.id, 'photos');
  }
}

/**
 * Set or clear the thumbnail photo ID on a harvest record.
 * Pass null to clear the thumbnail.
 */
export async function setHarvestThumbnail(
  harvestId: string,
  photoId: string | null,
): Promise<void> {
  const harvest = await db.harvestRecords.get(harvestId);
  if (!harvest) return;
  await db.harvestRecords.put({
    ...harvest,
    thumbnailPhotoId: photoId ?? undefined,
    updatedAt: new Date().toISOString(),
  });
}
